/**
 * The authority floor (FIELDSET §1.3, D-12.1-00-8): the columns NO lodge path writes. Verbatim from §1.3, per
 * register, by internal name. A column is on the floor when its FIELDSET §2 author is not the reporter.
 *
 * The one exception is the two lodge constants: a lodge writes `Status` = `New` and `ClosureStatus` = `Open` (and
 * its own `StateChange` sibling row) and nothing else on this floor. Any other value of either is a verb's to set.
 * `lodgeColumns ∩ floor = ∅` on every register is asserted by `fieldset.test.ts`.
 */

import { liveChoice } from './live';
import type { RecordRegister } from '../../types/records';

/** The columns no lodge path writes, per register (§1.3, verbatim; the counts are §1.3's). */
export const AUTHORITY_FLOOR: Readonly<Record<RecordRegister, ReadonlySet<string>>> = {
  /**
   * Incidents (97): §1.3's 93 line, then its separate "added 2026-10-25 by 13.2-01 (4, so 97 in all)" line (the last
   * four below; D-13.2-P-5, D-13.2-P-10). `InjuredWorkerName` is a lodge column, not on the floor (D-13.2-P-2).
   */
  incidents: new Set([
    'Status', 'ReportedBy', 'Reference', 'Severity', 'RecipientStream', 'ManagingUnit', 'PositionsResponsible',
    'ReportingType', 'ExternalNotified', 'ExternalOutcome', 'ResolveBy', 'RelevantPolicy', 'ExternalId',
    'ClosedOn', 'ClosingComments', 'SatisfactoryResolution', 'ResolutionApprovedBy', 'ReturnToWork',
    'ClosureStatus', 'EnrichmentPacket', 'EnrichedAt', 'EnrichmentSource', 'DraftCategory', 'DraftSeverity',
    'DraftStream', 'ReportabilityAssessment', 'Confidence', 'ReportabilityDecision', 'HarmConsequences',
    'TriageNote', 'TriagedAt', 'TriagedByUPN', 'NdisRiKind', 'NdisNoticeClass', 'SirsKind', 'SirsPriorityBasis',
    'SirsNeglectExclusionRef', 'AwareAt', 'NotificationDueAt', 'NdisInitialNoticeAt', 'NdisInitialNoticeMethod',
    'NotifierUPN', 'NotifierPosition', 'PoliceNotified', 'NdisFurtherNoticeAt', 'FurtherActionsProposed',
    'InfoWithheld', 'RegulatorReference', 'FinalReportRequired', 'FinalReportDueOn', 'FinalReportGivenAt',
    'SirsNoticeMethod', 'SirsNoticeAt', 'SirsAdditionalNoticeAt', 'CognitionLevel', 'PoliceNotifiedAt',
    'PoliceReference', 'OpenDisclosureUsed', 'OpenDisclosureAt', 'InvestigationRequired', 'InvestigationReason',
    'InvestigationFindings', 'Preventable', 'ManagementAssessment', 'CorrectiveActionRequired',
    'CorrectiveActionNature', 'AffectedViewsConsidered', 'HandlerUPN', 'ResolutionApprovedOn',
    'ResolutionApprovedByUPN', 'WorkSafeNotifiable', 'WorkSafeIncidentKind', 'WorkplaceType',
    'UnderHarmonyControl', 'WorkSafeNotifiedAt', 'WorkSafeNotifiedByUPN', 'WorkSafeReference',
    'WorkSafeWrittenRecordAt', 'InjuryNoticeReceivedOn', 'InjuryNoticeMethod', 'InjuryNoticeAcknowledgedOn',
    'ClaimReceivedOn', 'ClaimType', 'ClaimAcknowledgedOn', 'ClaimForwardedOn', 'MedicalCertificateReceivedOn',
    'MedicalCertificateForwardedOn', 'MentalInjuryClaim', 'MentalInjuryNotifiedOn', 'RtwKnowledgeOn',
    'RtwPlanningStartedOn', 'AllegationAgainstWorker', 'AllegationFirstHeldOn',
    // §1.3, added 2026-10-25 by 13.2-01
    'ReportabilityDraft', 'ReportabilityDraftNote', 'HrWhsTriageNeeded', 'DuplicateOfRef',
  ]),
  /** Complaints (28). */
  complaints: new Set([
    'Status', 'ManagingUnit', 'PositionsResponsible', 'RelevantPolicy', 'RelatedTo', 'ClosureStatus',
    'Reference', 'WithdrawnOn', 'AcknowledgedAt', 'ExpectedTimeframe', 'Priority', 'AssessmentNote',
    'InvestigationRequired', 'RegulatorReferred', 'RegulatorResponseDueOn', 'TargetResolutionOn', 'Outcome',
    'ClosedOn', 'OutcomeNotifiedAt', 'ReviewOptionsGiven', 'CommissionerRouteAdvisedOn', 'AdvocateInvolved',
    'OpenDisclosureUsed', 'EffectivenessEvaluation', 'ComplainantSatisfied', 'TrainingDelivered',
    'UnderlyingReason', 'SystemicIssue',
  ]),
  /** Feedback (16). */
  feedback: new Set([
    'Status', 'ManagingUnit', 'PositionsResponsible', 'RelevantPolicy', 'RelatedTo', 'ClosureStatus',
    'Reference', 'ClassifiedAsComplaint', 'WithdrawnOn', 'AcknowledgedAt', 'ResponseGivenAt', 'RaisesIssue',
    'CommissionerRouteAdvisedOn', 'ClosedOn', 'EffectivenessEvaluation', 'TrainingDelivered',
  ]),
  /** Risk Register (24). */
  risk: new Set([
    'Likelihood', 'Consequence', 'InherentRiskRating', 'ExistingControls', 'TreatmentActions',
    'ResidualLikelihood', 'ResidualConsequence', 'ResidualRiskRating', 'RiskOwner', 'ReviewDate', 'Status',
    'OriginatingItems', 'ClosureStatus', 'Reference', 'RiskRegisterType', 'RiskOwnerUPN', 'TreatmentUrgency',
    'HazardType', 'RiskAccepted', 'RiskAcceptedByUPN', 'RiskAcceptedOn', 'InsuranceChecked',
    'ResolutionAgreedOn', 'ResolutionCommunicatedOn',
  ]),
  /** Continuous Improvement (22). */
  ci: new Set([
    'OriginatingItems', 'Status', 'Priority', 'ManagingUnit', 'PositionsResponsible', 'StartOn', 'CompleteBy',
    'Outcome', 'RelevantPolicy', 'ClosedOn', 'ClosingComments', 'ResolutionApprovedBy', 'ClosureStatus',
    'Reference', 'BaselineSnapshot', 'ReviewDue', 'ReviewMethod', 'ReviewEvidence', 'ReviewVerdict',
    'ActionMode', 'OwnerUPN', 'ResolutionApprovedByUPN',
  ]),
};

/** On every audit sibling, a lodge never writes `LogKind`: it writes only its `StateChange` rows (§1.3). */
export const SIBLING_FLOOR: ReadonlySet<string> = new Set(['LogKind']);

/**
 * The lodge constants, the floor's only exception (§1.3): `Status` only `New`, `ClosureStatus` only `Open`. Each
 * value is checked to be a live choice of that register's column when this module loads.
 */
export const LODGE_CONSTANTS: Readonly<Record<RecordRegister, Readonly<{ Status: string; ClosureStatus: string }>>> =
  Object.fromEntries(
    (['incidents', 'complaints', 'feedback', 'risk', 'ci'] as const).map((r) => [
      r,
      {
        Status: liveChoice(r, 'Status', 'New')[0],
        ClosureStatus: liveChoice(r, 'ClosureStatus', 'Open')[0],
      },
    ]),
  ) as Record<RecordRegister, { Status: string; ClosureStatus: string }>;

/**
 * May a lodge path write `column` = `value` on `register`? False for every floor column, except the two constants
 * with their one allowed value.
 */
export function lodgeMayWrite(register: RecordRegister, column: string, value?: unknown): boolean {
  if (!AUTHORITY_FLOOR[register].has(column)) return true;
  const constants = LODGE_CONSTANTS[register];
  if (column === 'Status') return value === constants.Status;
  if (column === 'ClosureStatus') return value === constants.ClosureStatus;
  return false;
}
